// src/components/PoetryMinter.js

import React, { useState, useMemo, useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import QRCode from "react-qr-code";
import { createNft, mplTokenMetadata } from "@metaplex-foundation/mpl-token-metadata";
import { mplCore } from "@metaplex-foundation/mpl-core";
import {
    signerIdentity,
    generateSigner,
    percentAmount,
    createGenericFile,
} from "@metaplex-foundation/umi";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { bundlrUploader } from "@metaplex-foundation/umi-uploader-bundlr";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";

// Firebase imports
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp,updateDoc } from "firebase/firestore";

// Background options
import bgLow  from "../images/lowScore.jpg";
import bgMid  from "../images/midScore2.jpg";
import bgHigh from "../images/highScore.jpg";
import bgWin  from "../images/Win.jpg";

// Helper to load image for canvas
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload  = () => resolve(img);
        img.onerror = reject;
        img.src     = src;
        // uncomment if CORS issues:
        // img.crossOrigin = "anonymous";
    });
}

const generatePoem = (slots) => {
    const [s1, s2, s3] = slots;

    const line1Options = [
        `In code and light their paths align,`,
        `From bytes and dreams, the myths unwind,`,
        `Through neon clouds and cryptic time,`,
        `On quantum waves they cross the line,`,
        `A signal sparks in vast design,`
    ];
    const line2Options = [
        `A ${s1.Codename} walks with arcane sign.`,
        `The ${s1.Codename} speaks in silent mime.`,
        `A ${s1.Codename} dreams beneath the prime.`,
        `The ${s1.Codename} drifts through patterned rhyme.`,
        `A ${s1.Codename} lives outside of time.`
    ];

    const line3Options = [
        `By faith or fate, the ${s2.Occupation} flows,`,
        `The ${s2.Occupation} dances as time slows,`,
        `Each ${s2.Occupation} learns what no one knows,`,
        `The ${s2.Occupation} writes in shifting codes,`,
        `A ${s2.Occupation} hums as fire grows.`
    ];
    const line4Options = [
        `Where ${s2.Web3Alignment} wind forever blows.`,
        `Through ${s2.Web3Alignment} streets and open codes.`,
        `When ${s2.Web3Alignment} logic softly glows.`,
        `Along the paths that ${s2.Web3Alignment} chose.`,
        `Inside the dreams that ${s2.Web3Alignment} sows.`
    ];

    const line5Options = [
        `The ${s3.ReligiousPersuasion} hum a pastime tune,`,
        `The ${s3.ReligiousPersuasion} spin in silence soon,`,
        `The ${s3.ReligiousPersuasion} chant by twilight's moon,`,
        `The ${s3.ReligiousPersuasion} rest in sonic dune,`,
        `The ${s3.ReligiousPersuasion} kneel in afternoon.`
    ];
    const line6Options = [
        `Beneath the eye of Solana's moon.`,
        `On circuits cast in holy rune.`,
        `Inside the heart of woven bloom.`,
        `Beyond the code, they find their tune.`,
        `Between two blocks, a sacred boon.`
    ];

    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    return [
        pick(line1Options),
        pick(line2Options),
        "",
        pick(line3Options),
        pick(line4Options),
        "",
        pick(line5Options),
        pick(line6Options)
    ].join("\n");
};

export default function PoetryMinter({ slots, matchResult }) {
    const wallet = useWallet();
    const [status, setStatus] = useState("");
    const [minted, setMinted] = useState(false);
    const [spinSaved, setSpinSaved] = useState(false);
    const [spinImageUrl, setSpinImageUrl] = useState(null);
    const [isQRVisible, setIsQRVisible] = useState(false);
    const [isPoemVisible, setIsPoemVisible] = useState(true);
    const [docId, setDocId] = useState(null);


    // 1) Compute total score
    const totalScore = useMemo(
        () => slots.reduce((sum, slot) => sum + (slot.score || 0), 0),
        [slots]
    );

    // 2) Choose background image based on score/match
    const bgImage = useMemo(() => {
        if (matchResult === "Win")       return bgWin;
        if (totalScore >= 1700)            return bgHigh;
        if (totalScore >= 1200)            return bgMid;
        return bgLow;
    }, [totalScore, matchResult]);

    // 3) Save spin record & image to Firestore on spin completion
    useEffect(() => {
        if (!slots.length) return;

        const saveSpin = async () => {
            try {
                const poem   = generatePoem(slots);
                const lines  = poem.split("\n");
                const lineH  = 24;
                const canvas = document.createElement("canvas");
                canvas.width  = 500;
                canvas.height = lines.length * lineH + 40;
                const ctx = canvas.getContext("2d");

                // draw background
                const img = await loadImage(bgImage);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                // draw poem text
                ctx.fillStyle    = "black";
                ctx.font         = "18px serif";
                ctx.textBaseline = "top";
                lines.forEach((ln, i) => ctx.fillText(ln, 20, 20 + i * lineH));

                // canvas → blob → upload
                const blob    = await new Promise((res) => canvas.toBlob(res, "image/png"));
                const storage = getStorage();
                const ts      = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 13);
                const fname   = `spin-${ts}.png`;
                const storageRef = ref(storage, `poemSpins/${fname}`);
                await uploadBytes(storageRef, blob);
                const imageUrl = await getDownloadURL(storageRef);
                // const detailUrl = `${window.location.origin}/poem/${docRef.id}`;
                setSpinImageUrl(imageUrl);

                // save Firestore record
// save Firestore record first
                const docRef = await addDoc(collection(db, "poem_spins"), {
                    publicKey:  wallet.publicKey?.toString() || "",
                    timestamp:  serverTimestamp(),
                    totalScore,
                    matchResult,
                    slots:      slots.map((s, i) => ({ slot: i + 1, ...s })),
                    poem,
                    imageUrl,
                    savedAt:    "spin-complete",
                });

                setDocId(docRef.id); // <== store for QR code
// then update with detail URL based on doc ID
                const detailUrl = `${window.location.origin}/poem/${docRef.id}`;
                await updateDoc(docRef, { detailUrl });

                setSpinImageUrl(imageUrl);
                setSpinSaved(true);
            } catch (err) {
                console.error("Spin save failed", err);
            }
        };


        saveSpin();
    }, [slots, bgImage, totalScore, matchResult, wallet.publicKey]);

    // 4) Mint handler remains for NFT minting
    const handleMint = async () => {
        if (!wallet.connected) {
            alert("Connect wallet first.");
            return;
        }
        try {
            setStatus("Uploading & minting…");
            const poem = generatePoem(slots);
            const totalScore = slots.reduce((acc, img) => acc + img.score, 0);
            // const bgColor = matchResult === "Win" ? "#ffe066" : "#eaf4fc";

            // 1️⃣ Draw poem to off-screen canvas
            const lines = poem.split("\n");
            const lineHeight = 24;
            const canvas = document.createElement("canvas");
            canvas.width = 500;
            canvas.height = lines.length * lineHeight + 40;


            const ctx = canvas.getContext("2d");


            // ↪ load & draw the background _before_ drawing text
            try {
                const img = await loadImage(bgImage);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            } catch (err) {
                console.warn("Could not load background, falling back to white", err);
                ctx.fillStyle = "#fff";
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            ctx.fillStyle = "black";
            ctx.font      = "18px serif";
            ctx.textBaseline = "top";
            lines.forEach((line, i) => {
                ctx.fillText(line, 20, 20 + i * lineHeight);
            });

               // 2️⃣ load and draw background image
                   const img = new Image();
               img.crossOrigin = "anonymous";      // in case bundlr needs it
               img.src = bgImage;
               await new Promise((res, rej) => {
                     img.onload  = () => res();
                     img.onerror = () => rej(new Error("Background load failed"));
                   });
               // stretch it to fill the entire canvas
                   ctx.drawImage(img, 0, 0, canvas.width, canvas.height);


            ctx.fillStyle = "black";
            ctx.font = "18px serif";
            ctx.textBaseline = "top";
            lines.forEach((line, i) => {
                ctx.fillText(line, 20, 20 + i * lineHeight);
            });

            // 2️⃣ Canvas → PNG blob
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
            if (!blob) throw new Error("Failed to generate PNG");

            // 3️⃣ Upload PNG to Firebase Storage
            setStatus("Saving image to Firebase Storage…");
            const storage = getStorage();
            // Generate a sanitized filename
            const rawTs = new Date()
                .toISOString()
                .replace(/[-:.TZ]/g, "")
                .slice(0, 13);
            let filename = `ShlotPoem-${rawTs}.png`;
            const storageRef = ref(storage, `poemImages/${filename}`);
            await uploadBytes(storageRef, blob);
            const downloadUrl = await getDownloadURL(storageRef);

            // 4️⃣ Generate on-chain metadata
            setStatus("Uploading image to Arweave…");
            const umi = createUmi(process.env.REACT_APP_RPC_ENDPOINT)
                .use(signerIdentity(wallet))
                .use(mplCore())
                .use(mplTokenMetadata())
                .use(
                    bundlrUploader("https://node1.bundlr.network", {
                        identity: signerIdentity(wallet),
                    })
                )
                .use(walletAdapterIdentity(wallet));

            const arrayBuffer = await blob.arrayBuffer();
            const imageFile = createGenericFile(arrayBuffer, filename, {
                tags: [{ name: "Content-Type", value: "image/png" }],
            });
            const arweaveUri = (await umi.uploader.upload([imageFile]))[0];

            const name = filename.slice(0, 32);
            const metadata = {
                name,
                description: "A generative poem minted from Shlot Machines pull.",
                image: arweaveUri,
                attributes: slots.flatMap((slot, i) => [
                    { trait_type: `Slot ${i + 1} – Codename`, value: slot.Codename },
                    { trait_type: `Slot ${i + 1} – Occupation`, value: slot.Occupation },
                    { trait_type: `Slot ${i + 1} – Web3Alignment`, value: slot.Web3Alignment },
                    { trait_type: `Slot ${i + 1} – ReligiousPersuasion`, value: slot.ReligiousPersuasion },
                    { trait_type: `Slot ${i + 1} – Pastime`, value: slot.Pastime },
                ]),
                poem,
                matchResult,
                properties: { files: [{ uri: arweaveUri, type: "image/png" }], category: "image" },
            };

            // 5️⃣ Upload metadata + mint NFT
            setStatus("Uploading metadata…");
            const metadataUri = await umi.uploader.uploadJson(metadata);

            setStatus("Sending mint transaction…");
            const mint = generateSigner(umi);
            await createNft(umi, {
                mint,
                name: metadata.name,
                uri: metadataUri,
                sellerFeeBasisPoints: percentAmount(5.5),
            }).sendAndConfirm(umi);

                        // 6️⃣ Save record to Firestore (with server timestamp + URLs)
            setStatus("Saving record to Firestore…");
            await addDoc(collection(db, "poem_pulls"), {
                publicKey:  wallet.publicKey.toString(),
                timestamp:  serverTimestamp(),
                totalScore,
                matchResult,
                slots:      slots.map((s, i) => ({ slot: i + 1, ...s })),
                mintedAt:   "mint-complete",
            });

            setMinted(true);
            setStatus("🎉 Minted! 🎉");
        } catch (err) {
            console.error(err);
            setStatus(`Error: ${err.message}`);
        }
    };

    const poemText = generatePoem(slots);

    return (
        <div>
        <div
            style={{
                backgroundImage:    `url(${bgImage})`,
                backgroundSize:     "cover",
                backgroundPosition: "center",
                color:              "#000",
                padding:            "1.5rem",
                borderRadius:       "0.75rem",
                width:              "100%",
                maxWidth:           "100vw",
                margin:             "2rem auto",
                fontFamily:         "serif",
                whiteSpace:         "pre-wrap",
                lineHeight:         1.5,
            }}
        >
            {isPoemVisible && (
                <div>{poemText}</div>
            )}

        </div>
            <div className="poetry-controls" >
            <div style={{ textAlign: "center", marginTop: "1rem" }}>
                {/*{!minted && <button onClick={handleMint}>Mint This Poem</button>}*/}
                {spinSaved && spinImageUrl && (
                    <button
                        onClick={() => {
                            setIsQRVisible((prev) => !prev);
                            setIsPoemVisible((prev) => !prev);
                        }}
                        style={{ marginTop: "1rem" }}
                    >
                        {isQRVisible ? "Show Poem" : "Show QR Code"}
                    </button>
                )}
                {status && <p style={{ whiteSpace: "pre-wrap", marginTop: "0.5rem" }}>{status}</p>}
            </div>
                {spinSaved && spinImageUrl && isQRVisible && (
                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                        <p>Scan to view/download your poem image:</p>

                        <div
                            style={{
                                display: "inline-block",
                                background: "#fff",
                                padding: "1rem",
                                borderRadius: "12px",
                                boxShadow: "0 0 10px rgba(0,0,0,0.1)"
                            }}
                        >
                            <QRCode value={`https://shlotmachine.hersoid.com/poem/${docId}`} />
                        </div>
                    </div>
                )}
                {console.log(`https://shlotmachine.hersoid.com/poem/${docId}`)}

            {!spinSaved && <p>Saving spin…</p>}
            </div>
        </div>
    );
}
